import * as dcbm from '@leandredev/dcbm-ng-lib';

import { ApiHelper, form as Form, IHttpOption } from '@leandredev/dcbm-ng-lib';

import { Observable, of, map, switchMap } from 'rxjs';
import { interfaces as I, IHttpResult } from '@leandredev/dcbm-common-lib';
import { Injectable } from '@angular/core';
import { IActCreationParameters } from 'src/app/utils/interfaces';
import { IAct, IActType, IDcbmService, IDocLink, ITemplateAct, IUserClient } from '@leandredev/dcbm-common-lib/dist/interfaces';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ApiService } from '../api/api.service';
import { ProfileService } from '../profile/profile.service';
import { I_Data_Default_Types, I_DefaultTypes } from 'src/environments/interface';
import { SignInJwtService } from '../signIn-jwt/signIn-jwt.service';

@Injectable()
export class ActsService implements Partial<dcbm.service.ActsService> {
  private service: dcbm.service.ActsService;

  constructor(private http: HttpClient, private apiService: ApiService, private profileService: ProfileService, private signInJwt: SignInJwtService) {
    this.service = new dcbm.service.ActsService(this.http, true);
    this.service.urlCollection = `${environment.data.api}acts/`;
  }

  // API methods =======================================================================================================

  public create<T extends I.IAct, U extends I.IAct>(body: U, query: object, options?: IHttpOption): Observable<IHttpResult<T>> {
    return this.service.create(body, query, options);
  }

  public update<T extends I.IAct, U extends I.IAct>(body: U, query: object, options?: IHttpOption): Observable<IHttpResult<T>> {
    return this.service.update(body, query, options);
  }

  public delete<T extends I.IAct>(query: object, id: string, options?: IHttpOption): Observable<IHttpResult<T>> {
    return this.service.delete(query, id, options);
  }

  public deleteRgpdData(actId: string): Observable<IHttpResult<string>> {
    return this.service.deleteUserData({}, actId);
  }

  public find<T extends I.IAct>(query: object, id: string, options?: IHttpOption): Observable<IHttpResult<T>> {
    return this.service.find(query, id, options);
  }

  public findWithTemplate<T extends I.IAct>(query: object, id: string, options?: IHttpOption): Observable<IHttpResult<T>> {
    return this.service.findWithTemplate(query, id, options);
  }

  public getActByContractor<T extends I.IAct>(
    query: object,
    serviceId: string,
    contractorId: string,
    options?: IHttpOption
  ): Observable<IHttpResult<T>> {
    return this.service.getActsByContractorOf(query, serviceId, contractorId, options);
  }

  public getActsOf<T extends I.IAct>(query: object, id: string, options?: IHttpOption): Observable<IHttpResult<T>> {
    return this.service.getActsOf(query, id, options);
  }

  public findActsReferredBy<T extends I.IAct>(query: object, entityName: string, actId: string, options?: IHttpOption): Observable<IHttpResult<T>> {
    return this.service.findActsReferredBy(query, entityName, actId, options);
  }

  // ===================================================================================================================

  public getActsReferredBy(entityName: string, actId: string): Observable<IAct[]> {
    return this.findActsReferredBy({}, entityName, actId).pipe(ApiHelper.resultToArr('error'));
  }

  public defaultType: I_Data_Default_Types = environment.data.defaultTypes;

  public getList(listName: string): Observable<I.IName[]> {
    const list = environment.data.lists.find((list) => list.name === listName);

    if (!list) return of(null);

    return of(list.values);
  }

  public getActUrl(id: string): Observable<string> {
    return this.apiService.collections.Act.get(`_id/${id}/=/`).pipe(
      ApiHelper.resultToObj('Error'),
      map((act) => {
        const defaultTypes = environment.data.defaultTypes;

        let urlforEdit = '';

        Object.keys(defaultTypes).forEach((k) => {
          if (urlforEdit === '' && act.type.includes(defaultTypes[k].id)) {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions

            urlforEdit = `/data/${defaultTypes[k].class}/edit/${act._id}/`;
          }
        });

        return urlforEdit;
      })
    );
  }

  public getActsByIdsWithPopType(targets: string[]): Observable<IAct[]> {
    const query = `_id/${targets.join('/')}/${targets.length}/[/type/$pop`;

    return this.apiService.collections.Act.get(query).pipe(ApiHelper.resultToArrWEmptyError('Error'));
  }

  public getDefaultTypeById(id: string): Observable<I_DefaultTypes> {
    const foundDefaulTypeKey = Object.keys(this.defaultType).find((key) => {
      const defaultType = this.defaultType[key] as I_DefaultTypes;
      return defaultType.id == id;
    });

    return of(this.defaultType[foundDefaulTypeKey] as I_DefaultTypes);
  }

  public getActTypeForTemplate(templateRef: string): Observable<I.IActType[]> {
    return this.apiService.collections.ActType.get(`templateRef/${templateRef}/=/`).pipe(
      ApiHelper.resultToArrWEmptyError(`Aucun types disponilbe pour le modèle de reference: ${templateRef}`),
      map((type: I.IActType[]) => {
        return type;
      })
    );
  }

  public createAct(data: IActCreationParameters): Observable<I.IAct> {
    const form = new Form.Act();

    form._name.patchValue(data.name.replace('/', '_'));
    form._name.patchValue(data.name.replace('?', '_'));
    form._name.patchValue(data.name.replace('#', '_'));

    form._description.patchValue(data.description);

    form._templateVersion.patchValue(data.template.version);
    form._templateReference.patchValue(data.template._id);

    form._type.patchValue([data.template.defaultType]);
    form._service.patchValue(data.service);

    form._typeIsIdentity.patchValue(data.template.identityEntity || false);

    return this.getActType().pipe(
      map((actTypes) => {
        const foundActType = actTypes.find((actType) => {
          return actType.templateRef === data.template._id;
        });
        const typeIds = [data.template.defaultType, foundActType?._id].filter(Boolean) as string[];
        form._type.patchValue([...new Set(typeIds)]);
      }),
      switchMap(() => {
        return this.addAct(form.getRawValue());
      })
    );
  }

  public addAct(newActData: I.IAct): Observable<I.IAct> {
    return this.service.create(newActData, '', {}).pipe(ApiHelper.resultToObj('Erreur de création du dossier'));
  }

  public getTemplatActByDefaultTypeIds(ids: string[], userService: IDcbmService): Observable<I.ITemplateAct[]> {
    return this.apiService.collections.TemplateAct.get(`/defaultType/${ids.join('/')}/${ids.length}/[/`).pipe(
      ApiHelper.resultToArrWEmptyError('Aucun modéles pour la creation des actes'),
      map((templates) => {
        return templates.filter((template) => userService.templateList.includes(template._id));
      })
    );
  }

  public getTemplateActWithIds(ids: string[]): Observable<I.ITemplateAct[]> {
    return this.apiService.collections.TemplateAct.get(`_id/${ids.join('/')}/${ids.length}/[/`).pipe(
      ApiHelper.resultToArr('Aucun modéles pour la creation des actes')
    );
  }

  public getTemplateActFormActTypeIds(ids: string[]): Observable<I.ITemplateAct[]> {
    return this.apiService.collections.ActType.get(`_id/${ids.join('/')}/${ids.length}/[/templateRef/$pop/`).pipe(
      ApiHelper.resultToArr('Aucun modéles pour la creation des actes'),
      map((tempalteActTypes) => {
        return tempalteActTypes.map((t) => t.templateRef_pop);
      })
    );
  }

  public getTemplateActFormActTypeIdsFilterByService(ids: string[], userService: IDcbmService): Observable<I.ITemplateAct[]> {
    return this.getTemplateActFormActTypeIds(ids).pipe(
      map((templates) => {
        return templates.filter((template) => userService.templateList.includes(template._id));
      })
    );
  }

  public getTemplatAct(defaultTypes: string[]): Observable<I.ITemplateAct[]> {
    return this.apiService.collections.TemplateAct.get(`/defaultType/${defaultTypes.join('/')}/${defaultTypes.length}/[/`).pipe(
      ApiHelper.resultToArrWEmptyError('Aucun modéle pour la creation des actes')
    );
  }

  public getTemplatActByDefaultType(defaultTypeId: string): Observable<I.ITemplateAct[]> {
    return this.apiService.collections.TemplateAct.get(`/defaultType/${defaultTypeId}/=/`).pipe(
      ApiHelper.resultToArrWEmptyError('Aucun modéle pour la creation des actes')
    );
  }

  public getTemplatActByDefaultTypeFilterForUser(defaultTypeId: string): Observable<I.ITemplateAct[]> {
    return this.profileService.profile.pipe(
      switchMap((user) => {
        return this.getDCBMServicebyIds(user.service);
      }),
      switchMap((userServices) => {
        let mergedTemplateIds: string[] = [];

        userServices.forEach((service) => {
          mergedTemplateIds = mergedTemplateIds.concat(service.templateList);
        });

        return this.getTemplatActByDefaultType(defaultTypeId).pipe(
          map((templates) => {
            return templates.filter((template) => mergedTemplateIds.includes(template._id));
          })
        );
      })
    );
  }

  public getTemplatActByDefaultTypeFilterForService(defaultTypeId: string, userService: IDcbmService): Observable<I.ITemplateAct[]> {
    return this.getTemplatActByDefaultType(defaultTypeId).pipe(
      map((templates) => {
        return templates.filter((template) => userService.templateList.includes(template._id));
      })
    );
  }

  public getDCBMServicebyId(id: string): Observable<I.IDcbmService> {
    return this.apiService.collections.DcbmService.get(`_id/${id}/=/`).pipe(ApiHelper.resultToObj('Errueur du serveur'));
  }

  public getDCBMServicebyIds(ids: string[]): Observable<I.IDcbmService[]> {
    return this.apiService.collections.DcbmService.get(`_id/${ids.join('/')}/${ids.length}/[/`).pipe(ApiHelper.resultToArr('Errueur du serveur'));
  }

  public getDCBMService(): Observable<I.IDcbmService[]> {
    return this.apiService.collections.DcbmService.get('*').pipe(ApiHelper.resultToArr('Aucun service pour la création des actes'));
  }

  public getActType(): Observable<I.IActType[]> {
    return this.apiService.collections.ActType.get('readOnly/true/$bool/=/').pipe(
      ApiHelper.resultToArrWEmptyError('Aucun type pour la creation des actes')
    );
  }

  public getActTypesWithIds(typeIds: string[]): Observable<IActType[]> {
    let query = '*';

    if (typeIds && typeIds.length) {
      query = `_id/${typeIds.join('/')}/${typeIds.length}/[/`;
    }

    return this.apiService.collections.ActType.get(query).pipe(ApiHelper.resultToArrWEmptyError('Error'));
  }

  public getUserClientFormContractor(act: IAct): Observable<IUserClient[]> {
    const contractorIds = act.contrators;
    return this.apiService.collections.Act.get(`_id/${contractorIds.join('/')}/${contractorIds.length}/[/typeIsIdentity/true/=/&`).pipe(
      ApiHelper.resultToArr('Aucun dossier de contracteur'),
      switchMap((userIdentityActs) => {
        const userIdentityActID = userIdentityActs.map((e) => e._id);
        return this.apiService.collections.UserClient.get(`dcbmEntity/${userIdentityActID.join('/')}/${userIdentityActID.length}/[`).pipe(
          ApiHelper.resultToArr('Aucun Utilisateur trouvé')
        );
      })
    );
  }

  public getAllReadOnlyActTypes(): Observable<I.IActType[]> {
    return this.apiService.collections.ActType.get('/readOnly/true/=/').pipe(dcbm.ApiHelper.resultToArr('Aucun type trouve'));
  }

  public getData(query: string, selectTypes: string[]): Observable<IAct[]> {
    return this.apiService.collections.Act.get(
      `type/${selectTypes.join('/')}/${selectTypes.length}/[/name/${query}/i/$regex/&/10/$limit/templateReference/$pop/`
    ).pipe(dcbm.ApiHelper.resultToArr('Aucun type trouve'));
  }

  public downloadUserData(actId: string): void {
    const url = `${environment.data.api}acts/data/${encodeURIComponent(actId)}`;

    this.http.get(url, { observe: 'response', responseType: 'blob', headers: { JWT: this.signInJwt.token() } }).subscribe({
      next: (response) => {
        if (!response.body) return;

        const contentDisposition = response.headers.get('Content-Disposition');
        const filename = contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1] ?? 'documents.zip';
        const objectUrl = window.URL.createObjectURL(response.body);
        const link = document.createElement('a');

        link.href = objectUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
      },
      error: (error) => {
        console.error('Impossible de télécharger les données utilisateur.', error);
      }
    });
  }

  // public createTemplateAct(newTemplateActData: ITemplateAct): Observable<IActType> {
  //   return this.apiService.collections.TemplateAct.post(newTemplateActData).pipe(
  //     ApiHelper.resultToObj('Error de enregitrement'),
  //     switchMap((template) => {
  //       const associateTemplatetype: IActType = {
  //         templateRef: template._id,
  //         name: template.name
  //       };
  //       return this.apiService.collections.ActType.post(associateTemplatetype);
  //     })
  //   );
  // }

  public getDocumentLink(docLink: IDocLink): Observable<IDocLink> {
    const query = `_id/${docLink.act.ref}/=/`;

    return this.apiService.collections.Act.get(query).pipe(
      dcbm.ApiHelper.resultToObj('Auncun Act trouvé'),
      map((act) => {
        return act.documentLinks.find((_docLink) => {
          return _docLink.field.ref === docLink.field.ref;
        });
      })
    );
  }
}
